// rssDetection — Detecta RSS/Atom feed de uma URL de site. Combina:
//   1. Lookup table de domínios conhecidos (instantâneo, confiança alta)
//   2. Heurística por path comum (/feed/, /rss/, etc.) quando não conhecido
//
// V2 (futuro): auto-discovery via fetch do HTML procurando
// <link rel="alternate" type="application/rss+xml">.

// Mapa de hostname → função que constrói URL RSS. Função recebe a URL
// original (URL parsed) e retorna string RSS final.
const KNOWN_FEEDS = {
  'br.investing.com':         () => 'https://br.investing.com/rss/news.rss',
  'investing.com':            () => 'https://www.investing.com/rss/news.rss',
  'valor.globo.com':          () => 'https://valor.globo.com/rss/',
  'g1.globo.com':             () => 'https://g1.globo.com/rss/g1/',
  'globo.com':                () => 'https://oglobo.globo.com/rss/oglobo',
  'oglobo.globo.com':         () => 'https://oglobo.globo.com/rss/oglobo',
  'infomoney.com.br':         () => 'https://www.infomoney.com.br/feed/',
  'www.infomoney.com.br':     () => 'https://www.infomoney.com.br/feed/',
  'techcrunch.com':           () => 'https://techcrunch.com/feed/',
  'www.techcrunch.com':       () => 'https://techcrunch.com/feed/',
  'theverge.com':             () => 'https://www.theverge.com/rss/index.xml',
  'www.theverge.com':         () => 'https://www.theverge.com/rss/index.xml',
  'nytimes.com':              () => 'https://rss.nytimes.com/services/xml/rss/nyt/HomePage.xml',
  'www.nytimes.com':          () => 'https://rss.nytimes.com/services/xml/rss/nyt/HomePage.xml',
  'wired.com':                () => 'https://www.wired.com/feed/rss',
  'www.wired.com':            () => 'https://www.wired.com/feed/rss',
  'medium.com':               () => 'https://medium.com/feed',
  'dev.to':                   () => 'https://dev.to/feed',
  'news.ycombinator.com':     () => 'https://hnrss.org/frontpage',
  'reddit.com':               (u) => `${u.origin}${u.pathname.replace(/\/$/, '')}.rss`,
  'www.reddit.com':           (u) => `${u.origin}${u.pathname.replace(/\/$/, '')}.rss`,
  'arstechnica.com':          () => 'https://feeds.arstechnica.com/arstechnica/index',
  'www.arstechnica.com':      () => 'https://feeds.arstechnica.com/arstechnica/index',
  'bbc.com':                  () => 'https://feeds.bbci.co.uk/news/rss.xml',
  'www.bbc.com':              () => 'https://feeds.bbci.co.uk/news/rss.xml',
  'estadao.com.br':           () => 'https://www.estadao.com.br/rss/ultimas.xml',
  'www.estadao.com.br':       () => 'https://www.estadao.com.br/rss/ultimas.xml',
  'folha.uol.com.br':         () => 'https://feeds.folha.uol.com.br/poder/rss091.xml',
  'www1.folha.uol.com.br':    () => 'https://feeds.folha.uol.com.br/poder/rss091.xml',
  'exame.com':                () => 'https://exame.com/feed/',
  'tecmundo.com.br':          () => 'https://www.tecmundo.com.br/rss',
  'www.tecmundo.com.br':      () => 'https://www.tecmundo.com.br/rss',
  'olhardigital.com.br':      () => 'https://olhardigital.com.br/feed/',
  'www.olhardigital.com.br':  () => 'https://olhardigital.com.br/feed/',
};

/**
 * Já é uma URL de feed? Detecta pelo path ou content-type.
 * Conservador — só marca como feed se path bate com padrão conhecido.
 */
export function isLikelyFeedUrl(urlStr) {
  try {
    const u = new URL(urlStr);
    const p = u.pathname.toLowerCase();
    return p.endsWith('.rss') || p.endsWith('.xml') || p.endsWith('.atom')
      || p.includes('/feed') || p.includes('/rss')
      || p.includes('/atom');
  } catch { return false; }
}

/**
 * Sugere uma URL RSS pra uma URL de site. Não fetcha nada — pure function.
 *
 * @param {string} urlStr
 * @returns {{ suggested: string|null, confidence: 'known'|'guess'|null, reason: string }}
 */
export function suggestRss(urlStr) {
  if (!urlStr || typeof urlStr !== 'string') {
    return { suggested: null, confidence: null, reason: 'URL inválida' };
  }
  let u;
  try { u = new URL(urlStr); } catch {
    return { suggested: null, confidence: null, reason: 'URL inválida' };
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') {
    return { suggested: null, confidence: null, reason: 'Não é http(s)' };
  }
  // Já é feed → não sugere nada novo
  if (isLikelyFeedUrl(urlStr)) {
    return { suggested: null, confidence: null, reason: 'Já é feed' };
  }
  // Hostname conhecido
  const host = u.hostname.toLowerCase();
  const builder = KNOWN_FEEDS[host];
  if (builder) {
    return { suggested: builder(u), confidence: 'known', reason: `${host} tem RSS conhecido` };
  }
  // Heurística genérica — sugere /feed/ (o path mais comum em WordPress/Ghost/Medium)
  return {
    suggested: `${u.origin}/feed/`,
    confidence: 'guess',
    reason: 'Tentativa genérica (testar antes de salvar)',
  };
}

/**
 * Converte um batch de URLs pra RSS quando possível. Retorna lista
 * `{ original, suggested, confidence }` mantendo ordem.
 */
export function suggestRssBatch(urls) {
  return urls.map((url) => {
    const r = suggestRss(url);
    return { original: url, ...r };
  });
}
